import { diaNegocio, horaLocal, type AgendaDia, type CitaVista } from '@barbones/shared';

/** Columna de la vista día: un barbero (con su horario del día) o «Sin asignar» (`id: null`). */
export interface Columna {
  id: string | null;
  horario: AgendaDia['barberos'][number]['horario'] | null;
}

/** Estados que dejan el hueco libre: no bloquean el destino. */
const LIBERAN_HUECO = new Set<CitaVista['estado']>(['cancelada', 'no_show']);
/** Estados de una cita cuyo servicio ya inició: se mueve para corregir el registro, no para reprogramar. */
const INICIADAS = new Set<CitaVista['estado']>(['en_servicio', 'por_cobrar', 'finalizada']);

export const yaInicio = (cita: CitaVista) => INICIADAS.has(cita.estado);

export const aMinutos = (hora: string) => {
  const [h = 0, m = 0] = hora.split(':').map(Number);
  return h * 60 + m;
};
export const aHora = (minutos: number) => `${String(Math.floor(minutos / 60)).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}`;

const minutosDe = (iso: string) => aMinutos(horaLocal(new Date(iso)));
const seEmpalman = (a: [number, number], b: [number, number]) => a[0] < b[1] && b[0] < a[1];

/** Hora donde queda el borde superior del cuadro: la fila bajo el cursor menos las filas por encima del punto tomado. */
export function horaDestino(params: { horaFila: string; filasDesdeArriba: number; intervaloMin: number }): string {
  return aHora(aMinutos(params.horaFila) - params.filasDesdeArriba * params.intervaloMin);
}

/**
 * ¿Se puede soltar `cita` en `columna` a la `hora`? Reproduce las reglas del servidor como ayuda visual:
 * inicio entre entrada y salida, sin pisar la comida ni otras citas activas del barbero. «Sin asignar»
 * admite citas encimadas. Hoy no se puede soltar en horas que ya pasaron. Una cita que ya inició solo
 * va con un barbero y sin pisar sus otras citas: su hora y su barbero se corrigen aunque caigan fuera de horario.
 */
export function destinoValido(params: {
  columna: Columna;
  hora: string;
  cita: CitaVista;
  citas: CitaVista[];
  dia: string;
  ahora: Date;
  /** Primera y última fila de la rejilla: fuera de ellas la cita no se vería en la vista Día. */
  limites: { desde: string; hasta: string };
  /** Horario de la barbería: «Sin asignar» solo admite una hora nueva dentro de él (como el servidor). */
  sucursal?: { apertura: string; cierre: string };
}): boolean {
  const { columna, hora, cita, citas, dia, ahora, limites, sucursal } = params;
  const inicio = aMinutos(hora);
  if (inicio < aMinutos(limites.desde) || inicio > aMinutos(limites.hasta)) return false;
  const iniciada = yaInicio(cita);
  if (!iniciada && dia === diaNegocio(ahora) && inicio < aMinutos(horaLocal(ahora))) return false;
  if (columna.id === null) {
    if (iniciada) return false;
    const fuera = !!sucursal && (inicio < aMinutos(sucursal.apertura) || inicio > aMinutos(sucursal.cierre));
    return !fuera || inicio === minutosDe(cita.inicio);
  }

  const rango: [number, number] = [inicio, inicio + (minutosDe(cita.fin) - minutosDe(cita.inicio))];
  if (!iniciada) {
    const h = columna.horario;
    if (!h || h.esDescanso) return false;
    if (inicio < aMinutos(h.entrada) || inicio > aMinutos(h.salida)) return false;
    if (h.comidaInicio && h.comidaFin && seEmpalman(rango, [aMinutos(h.comidaInicio), aMinutos(h.comidaFin)])) return false;
  }
  return !citas.some((c) => c.id !== cita.id && c.barberoId === columna.id && !LIBERAN_HUECO.has(c.estado) && seEmpalman(rango, [minutosDe(c.inicio), minutosDe(c.fin)]));
}
